import axios from 'axios'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'

axios.defaults.withCredentials = false
axios.defaults.headers.common['Accept'] = 'application/json'

export interface SalesforceConfigResponse {
  client_id: string | null
  username: string | null
  instance_url: string | null
  is_active: boolean
  has_client_secret: boolean
  has_security_token: boolean
  has_password: boolean
}

export interface SalesforceStatusResponse {
  is_configured: boolean
  username: string | null
}

export interface SalesforceSaveConfigPayload {
  client_id: string
  client_secret?: string
  security_token?: string
  username: string
  password?: string
  instance_url?: string
}

export interface SalesforceImportCaseResponse {
  queued: boolean
  message: string
  synced?: number
  created?: number
  updated?: number
  skipped?: number
  import_date?: string
  errors?: string[]
}

export interface SalesforceImportPreviewAccount {
  account_id: string
  account_name: string
  cases_count: number
}

export interface SalesforceImportPreview {
  import_date: string
  accounts: SalesforceImportPreviewAccount[]
  saved_mappings: Array<{
    account_id: string
    account_name: string | null
    project_id: number | null
    project_name: string | null
  }>
  unassigned_cases_count: number
  total_cases_count: number
  mapped_cases_count: number
}

export interface SalesforceAccountOption {
  account_id: string
  account_name: string
}

export interface SalesforceAccountMapping {
  id: number
  account_id: string
  account_name: string | null
  client_id: number | null
  client_name: string | null
  project_id: number | null
  project_name: string | null
}

export interface SalesforceTokenResponse {
  token: string
  instance_id: number
  created_at: string
}

export interface SalesforceQueue {
  queue_id: string
  queue_name: string
  is_enabled: boolean
  project_id: number | null
  project_name: string | null
  client_id: number | null
  client_name: string | null
}

export interface SalesforceCatalogItem {
  id: number
  instance_id: number
  type: string
  service: string
  subservice: string
  service_product: string | null
  sla_hours: number
}

export interface SalesforceCatalogImportResult {
  imported: number
  errors: string[]
}

class SalesforceApiService {
  private getAuthHeaders() {
    const authKey = 'impersonating-auth-token'
    const authData = localStorage.getItem(authKey)

    let token: string | null = null
    if (authData) {
      try {
        const auth = JSON.parse(authData)
        token = auth.api_token
      } catch (error) {
        console.error('Erro ao parsear dados de autenticação:', error)
      }
    }

    if (!token) {
      token =
        localStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        localStorage.getItem('auth_token')
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  async getConfig(): Promise<SalesforceConfigResponse | null> {
    const response = await axios.get(`${API_BASE_URL}/api/salesforce/config`, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.success) {
      return response.data.data?.config ?? null
    }
    throw new Error(response.data.message || 'Erro ao carregar configuração')
  }

  async saveConfig(payload: SalesforceSaveConfigPayload): Promise<void> {
    const response = await axios.post(`${API_BASE_URL}/api/salesforce/config`, payload, {
      headers: this.getAuthHeaders(),
    })
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro ao salvar configuração')
    }
  }

  async getStatus(): Promise<SalesforceStatusResponse> {
    const response = await axios.get(`${API_BASE_URL}/api/salesforce/status`, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.success) {
      return response.data.data
    }
    throw new Error(response.data.message || 'Erro ao obter status')
  }

  async searchAccounts(query = ''): Promise<SalesforceAccountOption[]> {
    const response = await axios.get(`${API_BASE_URL}/api/salesforce/accounts`, {
      headers: this.getAuthHeaders(),
      params: query ? {q: query} : undefined,
    })
    if (response.data.status === 'success') {
      return response.data.data?.accounts ?? []
    }
    throw new Error(response.data.message || 'Erro ao buscar Accounts')
  }

  async getAccountMappings(): Promise<SalesforceAccountMapping[]> {
    const response = await axios.get(`${API_BASE_URL}/api/salesforce/account-mappings`, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.status === 'success') {
      return response.data.data?.mappings ?? []
    }
    throw new Error(response.data.message || 'Erro ao carregar mapeamentos')
  }

  async createAccountMapping(data: {
    account_id: string
    account_name?: string | null
    project_id: number
  }): Promise<SalesforceAccountMapping> {
    const response = await axios.post(`${API_BASE_URL}/api/salesforce/account-mappings`, data, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.status === 'success') {
      return response.data.data.mapping
    }
    throw new Error(response.data.message || 'Erro ao criar mapeamento')
  }

  async updateAccountMapping(
    id: number,
    data: {project_id: number; account_name?: string | null; account_id?: string}
  ): Promise<SalesforceAccountMapping> {
    const response = await axios.put(
      `${API_BASE_URL}/api/salesforce/account-mappings/${id}`,
      data,
      {headers: this.getAuthHeaders()}
    )
    if (response.data.status === 'success') {
      return response.data.data.mapping
    }
    throw new Error(response.data.message || 'Erro ao atualizar mapeamento')
  }

  async deleteAccountMapping(id: number): Promise<void> {
    const response = await axios.delete(`${API_BASE_URL}/api/salesforce/account-mappings/${id}`, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Erro ao remover mapeamento')
    }
  }

  async getImportPreview(): Promise<SalesforceImportPreview> {
    const response = await axios.get(`${API_BASE_URL}/api/salesforce/import/preview`, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.status === 'success') {
      const data = response.data.data ?? {}
      return {
        import_date: data.import_date ?? '',
        accounts: data.accounts ?? [],
        saved_mappings: data.saved_mappings ?? [],
        unassigned_cases_count: Number(data.unassigned_cases_count ?? 0),
        total_cases_count: Number(data.total_cases_count ?? 0),
        mapped_cases_count: Number(data.mapped_cases_count ?? 0),
      }
    }
    throw new Error(response.data.message || 'Erro ao carregar preview da importação')
  }

  async importCases(): Promise<SalesforceImportCaseResponse> {
    const response = await axios.post(
      `${API_BASE_URL}/api/salesforce/cases/import`,
      {},
      {headers: this.getAuthHeaders()}
    )
    if (response.data.status === 'success') {
      const data = response.data.data ?? {}
      return {
        queued: Boolean(response.data.queued),
        message: response.data.message || 'Importação concluída.',
        synced: Number(data.synced ?? 0),
        created: Number(data.created ?? 0),
        updated: Number(data.updated ?? 0),
        skipped: Number(data.skipped ?? 0),
        import_date: data.import_date,
        errors: Array.isArray(data.errors) ? data.errors : [],
      }
    }
    throw new Error(response.data.message || 'Erro ao importar Cases')
  }

  async getQueues(): Promise<SalesforceQueue[]> {
    const response = await axios.get(`${API_BASE_URL}/api/salesforce/queues`, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.status === 'success') {
      return response.data.data?.queues ?? []
    }
    throw new Error(response.data.message || 'Erro ao carregar filas')
  }

  async updateQueue(
    queueId: string,
    data: {queue_name?: string | null; is_enabled: boolean; project_id?: number | null}
  ): Promise<void> {
    const response = await axios.put(`${API_BASE_URL}/api/salesforce/queues/${queueId}`, data, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Erro ao atualizar fila')
    }
  }

  async generateToken(): Promise<SalesforceTokenResponse> {
    const response = await axios.post(
      `${API_BASE_URL}/api/salesforce/tokens/generate`,
      {},
      {headers: this.getAuthHeaders()}
    )
    if (response.data.status === 'success') {
      return response.data.data
    }
    throw new Error(response.data.message || 'Erro ao gerar token')
  }

  async getCatalog(): Promise<SalesforceCatalogItem[]> {
    const response = await axios.get(`${API_BASE_URL}/api/salesforce/catalog`, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.success) {
      return response.data.data ?? []
    }
    throw new Error(response.data.message || 'Erro ao carregar catálogo')
  }

  async createCatalogItem(data: {
    type: string
    service: string
    subservice: string
    service_product?: string | null
    sla_hours: number
  }): Promise<SalesforceCatalogItem> {
    const response = await axios.post(`${API_BASE_URL}/api/salesforce/catalog`, data, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.success) {
      return response.data.data
    }
    throw new Error(response.data.message || 'Erro ao criar item do catálogo')
  }

  async updateCatalogItem(
    id: number,
    data: {
      type: string
      service: string
      subservice: string
      service_product?: string | null
      sla_hours: number
    }
  ): Promise<SalesforceCatalogItem> {
    const response = await axios.put(`${API_BASE_URL}/api/salesforce/catalog/${id}`, data, {
      headers: this.getAuthHeaders(),
    })
    if (response.data.success) {
      return response.data.data
    }
    throw new Error(response.data.message || 'Erro ao atualizar item do catálogo')
  }

  async deleteCatalogItem(id: number): Promise<void> {
    const response = await axios.delete(`${API_BASE_URL}/api/salesforce/catalog/${id}`, {
      headers: this.getAuthHeaders(),
    })
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro ao remover item do catálogo')
    }
  }

  async importCatalog(
    rows: Array<{
      type: string
      service: string
      subservice: string
      service_product?: string | null
      sla_hours: number
    }>
  ): Promise<SalesforceCatalogImportResult> {
    const response = await axios.post(
      `${API_BASE_URL}/api/salesforce/catalog/import`,
      {rows},
      {headers: this.getAuthHeaders()}
    )
    if (response.data.success) {
      return response.data.data
    }
    throw new Error(response.data.message || 'Erro ao importar catálogo')
  }
}

export const salesforceApiService = new SalesforceApiService()
